import { getAffiliateLinkProps } from 'util/affiliates/amazon';

/**
 * returns an array of all non-null recipe supply item ids from all groups'
 * items. we don't need to worry about de-duping because we're not gonna have
 * the same recipe supply item twice.
 *
 * @export
 * @param {Object[]} [supplyGroups=[]]
 * @param {Object} [supplyGroups.items]
 * @param {String} [supplyGroups.items[].recipeSupplyItemId]
 * @returns {String[]} array of recipe supply item IDs
 */
export function getRecipeSupplyItemIds(supplyGroups = []) {
  return supplyGroups
    .reduce((groupAcc, g) => {
      const groupIds = g.items.map(i => i.recipeSupplyItemId);
      return groupAcc.concat(groupIds);
    }, [])
    .filter(Boolean);
}

/**
 * converts an array of affiliateLink objects from an affiliateLinks GQL query
 * result to a map of id to URL. if the URL exists, tag it with an amazon
 * affiliate code
 *
 * @param {Object[]} [affiliateLinks=[]]
 * @param {String} [affiliateLinks.relatedItemId]
 * @param {String} [affiliateLinks.url]
 * @returns {Object} mapping of relatedItemId to (processed) URL
 */
export function processAffiliateLinks(affiliateLinks = []) {
  return affiliateLinks.reduce(
    (acc, curr) => ({
      ...acc,
      ...(curr &&
        curr.relatedItemId && {
          [curr.relatedItemId]: getAffiliateLinkProps(curr.url).href
        })
    }),
    {}
  );
}

/**
 * enrich the recipe supplies (items in supply groups) with affiliate link data.
 *
 * @export
 * @param {Object[]} [supplyGroups=[]]
 * @param {Object[]} [supplyGroups.items]
 * @param {String} [supplyGroups.items[].recipeSupplyItemId]
 * @param {Object} [affiliateLinks={}] map with id as key (string) and URL as value
 * @returns {Object[]} copy of supplyGroups, updated with affiliate link data
 */
export function enrichSuppliesWithAffiliateLinks(
  supplyGroups = [],
  affiliateLinks = {}
) {
  return supplyGroups.map(g => {
    const items = g.items.map(i => {
      // look up affiliate link based on recipeSupplyItemId
      const url = i.recipeSupplyItemId
        ? // ensure we always return null instead of undefined
          affiliateLinks[i.recipeSupplyItemId] || null
        : null;

      // add affiliateLink prop, which was previously present courtesy of
      // graphql field delegation.
      return {
        ...i,
        ...{ affiliateLink: { url } }
      };
    });

    // overwrite existing items with new ones
    return {
      ...g,
      ...{ items }
    };
  });
}
